<template>
  <div v-if="elementIdot" ref="parent">
    <Badge class="idot" severity="info" v-model="elementIdot" value="i"
           @click="toggle"/>
    <OverlayPanel ref="op" :append-to="'body'">

    <div class="idotPanel">
        <div v-if="elementIdotImage"
             :style="{
          width: elementIdotImageWidth ? `${elementIdotImageWidth}px` : '100px',
          height: elementIdotImageHeight ? `${elementIdotImageHeight}px` : '',
          marginBottom: elementIdotMessage ? '10px': ''
        }">
          <ImageWidget :elementData="{ idotImage: true, idotImageUrl: elementIdotImageUrl }" @idotImageChosen="chosen"/>
        </div>
        <div class="idotMessage" v-html="elementIdotMessage" :style="{ fontFamily: fontName }"></div>
      </div>
    </OverlayPanel>
  </div>
</template>

<script setup lang="ts">
import {computed, toRefs, ref, onMounted, nextTick} from "vue";
import {storeToRefs} from "pinia";
import {useMainStore, useSlidesStore} from "@/store";
import ImageWidget from "@/views/components/element/DragDropElement/components/ImageWidget.vue";
import DOMPurify from 'dompurify';

const props = defineProps({
  elementData: {
    type: Object
  }
})
const { theme } = storeToRefs(useSlidesStore())
const { editor } = storeToRefs(useMainStore())
const {elementData} = toRefs(props)
const op = ref()
const parent = ref()
let wip = false

const toggle = (event) => {
    wip = true
    if (editor.value) {
      op.value.toggle(event)
    }
    else {
      const rect = parent.value.getBoundingClientRect();
      op.value.toggle({ event: event, currentTarget: document.body })

      nextTick(() => {
        op.value.container.style.left = rect.left + window.scrollX + 'px'
        op.value.container.style.top = rect.bottom + window.scrollY + 'px'
      })
    }

    setTimeout(() => { wip = false}, 500)
}

onMounted(async () => {
  document.body.addEventListener("click",  (event) => {
    if (!wip && op.value && event.target.tagName !== 'IMG') {
      op.value.hide()
    }
  })
})

const elementIdot = computed(() => elementData.value?.idotButton?.idot ?? false)
const idotMessage = computed(() => DOMPurify.sanitize(elementData.value?.idotButton?.idotMessage) ?? '');
const elementIdotMessage = computed(() => idotMessage.value.length > 0 ? addTargetBlank(idotMessage.value) : idotMessage.value)
const elementIdotImage = computed(() => elementData.value?.idotButton?.idotImage ?? false)
const elementIdotImageUrl = computed(() => elementData.value?.idotButton?.idotImageUrl ?? null)
const elementIdotImageWidth = computed(() => elementData.value?.idotButton?.idotImageWidth ?? null)
const elementIdotImageHeight = computed(() => elementData.value?.idotButton?.idotImageHeight?? null)
const elementIdotTextColor = computed(() => elementData.value?.idotButton?.style?.color ?? '#617684')
const elementIdotBackgroundColor = computed(() => elementData.value?.idotButton?.style?.backgroundColor ?? 'rgb(255, 255, 255)')
const elementIdotBorderColor = computed(() => elementData.value?.idotButton?.style?.borderColor ?? '#617684')
const fontName = computed(() => `"${theme.value.fontName}"` ?? '')

const addTargetBlank = (htmlString: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  doc.querySelectorAll('a').forEach(link => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });

  return doc.body.innerHTML;
}

const chosen = (img: string) => {
  elementData.value.idotButton.idotImageUrl = img
}
</script>

<style scoped lang="scss">
.idot {
  position: relative;
  top: -15px;
  margin-left: 10px;
}

.idot:hover {
  cursor: pointer;
}

.idot {
  background-color: v-bind(elementIdotBackgroundColor);
  border: 1px solid v-bind(elementIdotBorderColor);
  color: v-bind(elementIdotTextColor);
}

:deep(.p-overlaypanel) {
  top: auto !important;
  left: auto !important;
}

:deep(.p-overlaypanel-content) {
  white-space: nowrap;
}

:deep(.p-overlaypanel > .p-overlaypanel-content) {
  white-space: nowrap;
  display: flex;
}

.idotMessage p {
  margin: 0;
  padding: 0;
}
</style>
